<template>
  <div class="controllo-corsi-container">
    <div class="card mb-4 shadow-1 border-round">
      <div class="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3">
        <div>
          <h2 class="m-0 text-2xl font-bold text-900 flex align-items-center gap-2">
            <i class="pi pi-exclamation-triangle text-orange-500 text-3xl"></i>
            Controllo Pagamenti Corsi
          </h2>
          <p class="text-600 m-0 mt-1">
            Analisi incassi attesi vs incassati per corso e monitoraggio delle rate scadute non saldate.
          </p>
        </div>
        <Button 
          label="Aggiorna Dati" 
          icon="pi pi-refresh" 
          class="p-button-outlined p-button-secondary" 
          :loading="loading" 
          @click="loadData" 
        />
      </div>

      <!-- KPI Summary Cards -->
      <div class="grid mt-4">
        <div class="col-12 sm:col-6 lg:col-3">
          <div class="surface-card p-3 border-1 surface-border border-round flex flex-column justify-content-between h-full">
            <div class="flex justify-content-between mb-3">
              <div>
                <span class="block text-500 font-medium mb-1">Totale Scaduto Non Pagato</span>
                <div class="text-900 font-bold text-2xl text-red-600">
                  {{ formatCurrency(totalScadutoNonPagato) }}
                </div>
              </div>
              <div class="flex align-items-center justify-content-center bg-red-100 border-round" style="width: 2.5rem; height: 2.5rem">
                <i class="pi pi-dollar text-red-600 text-xl"></i>
              </div>
            </div>
            <span class="text-500 text-sm">Somma rate scadute ad oggi in arretrato</span>
          </div>
        </div>

        <div class="col-12 sm:col-6 lg:col-3">
          <div class="surface-card p-3 border-1 surface-border border-round flex flex-column justify-content-between h-full">
            <div class="flex justify-content-between mb-3">
              <div>
                <span class="block text-500 font-medium mb-1">Corsi Problematici</span>
                <div class="text-900 font-bold text-2xl text-orange-600">
                  {{ corsiProblematiciCount }} <span class="text-500 text-base font-normal">/ {{ courses.length }}</span>
                </div>
              </div>
              <div class="flex align-items-center justify-content-center bg-orange-100 border-round" style="width: 2.5rem; height: 2.5rem">
                <i class="pi pi-exclamation-circle text-orange-600 text-xl"></i>
              </div>
            </div>
            <span class="text-500 text-sm">Corsi con almeno una rata scaduta non pagata</span>
          </div>
        </div>

        <div class="col-12 sm:col-6 lg:col-3">
          <div class="surface-card p-3 border-1 surface-border border-round flex flex-column justify-content-between h-full">
            <div class="flex justify-content-between mb-3">
              <div>
                <span class="block text-500 font-medium mb-1">Incassato Complessivo</span>
                <div class="text-900 font-bold text-2xl text-green-600">
                  {{ formatCurrency(totaleIncassatoGenerale) }}
                </div>
              </div>
              <div class="flex align-items-center justify-content-center bg-green-100 border-round" style="width: 2.5rem; height: 2.5rem">
                <i class="pi pi-check-circle text-green-600 text-xl"></i>
              </div>
            </div>
            <span class="text-500 text-sm">Su atteso di {{ formatCurrency(totaleDaIncassareGenerale) }} ({{ percentualeIncassatoGenerale }}%)</span>
          </div>
        </div>

        <div class="col-12 sm:col-6 lg:col-3">
          <div class="surface-card p-3 border-1 surface-border border-round flex flex-column justify-content-between h-full">
            <div class="flex justify-content-between mb-3">
              <div>
                <span class="block text-500 font-medium mb-1">Atleti in Ritardo</span>
                <div class="text-900 font-bold text-2xl text-purple-600">
                  {{ atletiInRitardoTotaliCount }}
                </div>
              </div>
              <div class="flex align-items-center justify-content-center bg-purple-100 border-round" style="width: 2.5rem; height: 2.5rem">
                <i class="pi pi-users text-purple-600 text-xl"></i>
              </div>
            </div>
            <span class="text-500 text-sm">Numero atleti totali con arretrati nei corsi</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Month Selector Bar (Mesi da Settembre a Giugno) -->
    <div class="card mb-4 p-3 shadow-1 border-round surface-card">
      <div class="flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center gap-2 mb-3">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-calendar text-blue-600 text-xl"></i>
          <span class="font-bold text-800 text-lg">Mese di Riferimento Calcolo Ritardi:</span>
        </div>
        <Tag severity="info" class="px-3 py-1 text-sm font-semibold">
          Scadenze considerate fino al: {{ selectedMonthLabel }}
        </Tag>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button 
          v-for="m in seasonMonths" 
          :key="m.value" 
          :label="m.shortLabel" 
          :class="m.value === selectedMonth ? 'p-button-primary font-bold shadow-2' : 'p-button-outlined p-button-secondary'"
          class="p-button-sm px-3 py-2 border-round-lg"
          @click="selectMonth(m.value)"
        />
      </div>
    </div>

    <!-- Filters & Search Toolbar -->
    <div class="card mb-4 p-3 shadow-1 border-round surface-card">
      <div class="flex flex-column md:flex-row justify-content-between align-items-stretch md:align-items-center gap-3">
        <div class="flex flex-column sm:flex-row align-items-stretch sm:align-items-center gap-3 flex-1">
          <span class="p-input-icon-left w-full sm:w-20rem">
            <i class="pi pi-search"></i>
            <InputText v-model="searchQuery" placeholder="Cerca corso..." class="w-full" />
          </span>

          <div class="flex align-items-center gap-2">
            <Checkbox v-model="onlyProblematic" :binary="true" inputId="problematicFilter" id="problematicFilter" />
            <label for="problematicFilter" class="cursor-pointer text-700 font-medium select-none">
              Solo corsi problematici (con ritardi)
            </label>
          </div>
        </div>

        <div class="flex align-items-center gap-2">
          <label class="text-600 font-medium text-sm whitespace-nowrap">Ordina per:</label>
          <Dropdown v-model="sortBy" :options="sortOptions" optionLabel="label" optionValue="value" class="w-14rem" />
        </div>
      </div>
    </div>

    <Loader v-if="loading" />

    <div v-else-if="filteredCourses.length === 0" class="card text-center p-5 surface-card border-round">
      <i class="pi pi-inbox text-500 text-5xl mb-3"></i>
      <h3 class="text-700 m-0">Nessun corso trovato</h3>
      <p class="text-500 mt-2 mb-0">Non ci sono corsi che corrispondono ai criteri di ricerca impostati.</p>
    </div>

    <!-- Courses List / Accordion -->
    <div v-else class="flex flex-column gap-3">
      <Accordion :multiple="true" :activeIndex="activeAccordionIndexes">
        <AccordionTab v-for="course in filteredCourses" :key="course.id">
          <template #header>
            <div class="flex flex-column lg:flex-row justify-content-between align-items-start lg:align-items-center w-full pr-3 gap-3">
              <div class="flex flex-wrap align-items-center gap-3 mr-4 lg:mr-6 mb-2 lg:mb-0">
                <router-link 
                  :to="'/corso/' + course.id + '/dettagli'" 
                  class="text-xl font-bold text-blue-700 hover:underline no-underline mr-3"
                  @click.stop
                >
                  {{ course.nome }}
                </router-link>
                
                <Tag 
                  :severity="course.is_problematico ? 'danger' : 'success'" 
                  :value="course.is_problematico ? 'Problematico (' + course.num_atleti_in_ritardo + ' atleti in ritardo)' : 'In Regola'"
                  class="px-2 py-1"
                />
              </div>

              <div class="flex flex-wrap align-items-center gap-5 lg:gap-6 text-sm">
                <div class="px-2">
                  <span class="text-500 block">Atteso:</span>
                  <span class="font-semibold text-900">{{ formatCurrency(course.totale_da_incassare) }}</span>
                </div>
                <div class="px-2">
                  <span class="text-500 block">Incassato:</span>
                  <span class="font-semibold text-green-600">{{ formatCurrency(course.totale_incassato) }}</span>
                </div>
                <div class="px-2">
                  <span class="text-500 block">In Arretrato:</span>
                  <span :class="course.totale_scaduto_non_pagato > 0 ? 'font-bold text-red-600' : 'font-semibold text-600'">
                    {{ formatCurrency(course.totale_scaduto_non_pagato) }}
                  </span>
                </div>
                <div class="px-2" style="min-width: 90px;">
                  <span class="text-500 block">% Saldata:</span>
                  <span class="font-bold" :class="course.percentuale_incassato < 80 ? 'text-red-600' : 'text-green-600'">
                    {{ course.percentuale_incassato }}%
                  </span>
                </div>
              </div>
            </div>
          </template>

          <TabView class="mt-2">
            <!-- TAB 1: Riepilogo Mensile -->
            <TabPanel header="Riepilogo Mensile">
              <DataTable :value="course.mesi" responsiveLayout="scroll" class="p-datatable-sm p-datatable-gridlines">
                <Column field="label" header="Mese">
                  <template #body="{ data }">
                    <span class="font-medium text-800">{{ data.label }}</span>
                  </template>
                </Column>

                <Column field="da_incassare" header="Da Incassare" headerClass="text-right" bodyClass="text-right">
                  <template #body="{ data }">
                    {{ formatCurrency(data.da_incassare) }}
                  </template>
                </Column>

                <Column field="incassato" header="Incassato" headerClass="text-right" bodyClass="text-right">
                  <template #body="{ data }">
                    <span class="text-green-600 font-medium">{{ formatCurrency(data.incassato) }}</span>
                  </template>
                </Column>

                <Column field="scaduto_non_pagato" header="Scaduto / In Arretrato" headerClass="text-right" bodyClass="text-right">
                  <template #body="{ data }">
                    <span :class="data.scaduto_non_pagato > 0 ? 'font-bold text-red-600' : 'text-500'">
                      {{ formatCurrency(data.scaduto_non_pagato) }}
                    </span>
                  </template>
                </Column>

                <Column header="Stato Mese" headerClass="text-center" bodyClass="text-center">
                  <template #body="{ data }">
                    <Tag 
                      v-if="data.da_incassare === 0" 
                      severity="secondary" 
                      value="Nessuna scadenza" 
                    />
                    <Tag 
                      v-else-if="data.scaduto_non_pagato === 0" 
                      severity="success" 
                      value="Saldo OK" 
                    />
                    <Tag 
                      v-else-if="data.incassato > 0" 
                      severity="warning" 
                      value="Parzialmente In Arretrato" 
                    />
                    <Tag 
                      v-else 
                      severity="danger" 
                      value="In Arretrato" 
                    />
                  </template>
                </Column>
              </DataTable>
            </TabPanel>

            <!-- TAB 2: Atleti in Ritardo -->
            <TabPanel :header="'Atleti in Ritardo (' + course.num_atleti_in_ritardo + ')'">
              <div v-if="course.dettagli_ritardi.length === 0" class="p-3 text-500 text-center">
                Nessun atleta in ritardo nei pagamenti per questo corso.
              </div>

              <DataTable v-else :value="course.dettagli_ritardi" responsiveLayout="scroll" class="p-datatable-sm p-datatable-striped">
                <Column field="atleta" header="Atleta">
                  <template #body="{ data }">
                    <a :href="'/atleta/' + data.id_atleta + '/dettagli'" target="_blank" class="font-bold text-blue-700 hover:underline">
                      {{ data.atleta }}
                    </a>
                  </template>
                </Column>

                <Column field="n_rata" header="Rata N." headerClass="text-center" bodyClass="text-center">
                  <template #body="{ data }">
                    <span class="font-medium">Rata {{ data.n_rata || '-' }}</span>
                  </template>
                </Column>

                <Column field="importo_richiesto" header="Importo Richiesto" headerClass="text-right" bodyClass="text-right">
                  <template #body="{ data }">
                    {{ formatCurrency(data.importo_richiesto) }}
                  </template>
                </Column>

                <Column field="importo_incassato" header="Incassato" headerClass="text-right" bodyClass="text-right">
                  <template #body="{ data }">
                    {{ formatCurrency(data.importo_incassato) }}
                  </template>
                </Column>

                <Column field="importo_residuo" header="Importo In Arretrato" headerClass="text-right" bodyClass="text-right">
                  <template #body="{ data }">
                    <span class="font-bold text-red-600">{{ formatCurrency(data.importo_residuo) }}</span>
                  </template>
                </Column>

                <Column field="data_scadenza" header="Data Scadenza" headerClass="text-center" bodyClass="text-center">
                  <template #body="{ data }">
                    {{ formatDate(data.data_scadenza) }}
                  </template>
                </Column>

                <Column field="giorni_ritardo" header="Ritardo" headerClass="text-center" bodyClass="text-center">
                  <template #body="{ data }">
                    <Tag severity="danger" :value="data.giorni_ritardo + ' gg'" />
                  </template>
                </Column>

                <Column header="Azione" headerClass="text-center" bodyClass="text-center">
                  <template #body="{ data }">
                    <Button 
                      icon="pi pi-external-link" 
                      class="p-button-rounded p-button-text p-button-info" 
                      v-tooltip.top="'Apri scheda atleta'"
                      @click="goToAtleta(data.id_atleta)"
                    />
                  </template>
                </Column>
              </DataTable>
            </TabPanel>
          </TabView>
        </AccordionTab>
      </Accordion>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';
import Loader from '@/components/Loader.vue';
import Service from '@/services/levante-api/levante-api';
import moment from 'moment';

export default {
  name: 'ControlloCorsi',
  components: {
    Loader
  },
  setup() {
    const toast = useToast();
    const loading = ref(true);
    const courses = ref([]);
    const searchQuery = ref('');
    const onlyProblematic = ref(false);
    const sortBy = ref('overdue_desc');

    const defaultTargetMonth = moment().subtract(1, 'month').format('YYYY-MM');
    const selectedMonth = ref(defaultTargetMonth);

    const sortOptions = [
      { label: 'Maggior importo in ritardo', value: 'overdue_desc' },
      { label: 'Nome corso (A-Z)', value: 'name_asc' },
      { label: 'Minore % incassato', value: 'pct_asc' }
    ];

    const seasonMonths = computed(() => {
      const now = moment();
      const currentMonthNum = now.month() + 1;
      const currentYear = now.year();

      const startYear = currentMonthNum >= 9 ? currentYear : currentYear - 1;
      const endYear = startYear + 1;

      const monthsList = [
        { name: 'Settembre', m: '09', year: startYear },
        { name: 'Ottobre', m: '10', year: startYear },
        { name: 'Novembre', m: '11', year: startYear },
        { name: 'Dicembre', m: '12', year: startYear },
        { name: 'Gennaio', m: '01', year: endYear },
        { name: 'Febbraio', m: '02', year: endYear },
        { name: 'Marzo', m: '03', year: endYear },
        { name: 'Aprile', m: '04', year: endYear },
        { name: 'Maggio', m: '05', year: endYear },
        { name: 'Giugno', m: '06', year: endYear }
      ];

      if (moment().subtract(1, 'month').format('MM') === '08') {
        monthsList.unshift({ name: 'Agosto', m: '08', year: startYear });
      }

      return monthsList.map(item => {
        const ym = `${item.year}-${item.m}`;
        return {
          value: ym,
          label: `${item.name} ${item.year}`,
          shortLabel: `${item.name.substring(0, 3)} ${item.year.toString().slice(-2)}`
        };
      });
    });

    const selectedMonthLabel = computed(() => {
      const found = seasonMonths.value.find(m => m.value === selectedMonth.value);
      if (found) return found.label;
      return moment(selectedMonth.value, 'YYYY-MM').locale('it').format('MMMM YYYY');
    });

    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);

    const loadData = async () => {
      loading.value = true;
      try {
        const res = await service.getControlloCorsiPagamenti({
          target_month: selectedMonth.value
        });
        courses.value = res.items || [];
      } catch (err) {
        console.error('Errore nel caricamento controllo corsi:', err);
      } finally {
        loading.value = false;
      }
    };

    const selectMonth = (val) => {
      if (selectedMonth.value === val) return;
      selectedMonth.value = val;
      const found = seasonMonths.value.find(m => m.value === val);
      if (toast) {
        toast.add({
          severity: 'info',
          summary: 'Ricalcolo in corso',
          detail: `Calcolo ritardi al mese di ${found ? found.label : val}...`,
          life: 2000
        });
      }
      loadData();
    };

    onMounted(() => {
      loadData();
    });

    const formatCurrency = (val) => {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
      }).format(val || 0);
    };

    const formatDate = (val) => {
      if (!val) return '-';
      return moment(val).format('DD/MM/YYYY');
    };

    const goToAtleta = (idatleta) => {
      window.open(`/atleta/${idatleta}/dettagli`, '_blank');
    };

    // Computed totals
    const totalScadutoNonPagato = computed(() => {
      return courses.value.reduce((sum, c) => sum + (c.totale_scaduto_non_pagato || 0), 0);
    });

    const corsiProblematiciCount = computed(() => {
      return courses.value.filter(c => c.is_problematico).length;
    });

    const totaleDaIncassareGenerale = computed(() => {
      return courses.value.reduce((sum, c) => sum + (c.totale_da_incassare || 0), 0);
    });

    const totaleIncassatoGenerale = computed(() => {
      return courses.value.reduce((sum, c) => sum + (c.totale_incassato || 0), 0);
    });

    const percentualeIncassatoGenerale = computed(() => {
      if (totaleDaIncassareGenerale.value === 0) return 100;
      return Math.round((totaleIncassatoGenerale.value / totaleDaIncassareGenerale.value) * 100);
    });

    const atletiInRitardoTotaliCount = computed(() => {
      const set = new Set();
      courses.value.forEach(c => {
        (c.dettagli_ritardi || []).forEach(r => {
          set.add(r.id_atleta);
        });
      });
      return set.size;
    });

    const filteredCourses = computed(() => {
      let list = [...courses.value];

      if (searchQuery.value.trim()) {
        const q = searchQuery.value.toLowerCase().trim();
        list = list.filter(c => c.nome.toLowerCase().includes(q));
      }

      if (onlyProblematic.value) {
        list = list.filter(c => c.is_problematico);
      }

      if (sortBy.value === 'overdue_desc') {
        list.sort((a, b) => b.totale_scaduto_non_pagato - a.totale_scaduto_non_pagato);
      } else if (sortBy.value === 'name_asc') {
        list.sort((a, b) => a.nome.localeCompare(b.nome));
      } else if (sortBy.value === 'pct_asc') {
        list.sort((a, b) => a.percentuale_incassato - b.percentuale_incassato);
      }

      return list;
    });

    const activeAccordionIndexes = computed(() => {
      if (onlyProblematic.value) {
        return filteredCourses.value.map((_, idx) => idx);
      }
      return [];
    });

    return {
      loading,
      courses,
      searchQuery,
      onlyProblematic,
      sortBy,
      sortOptions,
      selectedMonth,
      seasonMonths,
      selectedMonthLabel,
      selectMonth,
      loadData,
      formatCurrency,
      formatDate,
      goToAtleta,
      totalScadutoNonPagato,
      corsiProblematiciCount,
      totaleDaIncassareGenerale,
      totaleIncassatoGenerale,
      percentualeIncassatoGenerale,
      atletiInRitardoTotaliCount,
      filteredCourses,
      activeAccordionIndexes
    };
  }
};
</script>

<style scoped>
.controllo-corsi-container {
  padding: 1rem;
}
.whitespace-nowrap {
  white-space: nowrap;
}
</style>
